/**
 * Compile UtilityBar filter chips into strawberry-orm filter inputs
 * ({ field, all, any, object } @oneOf trees).
 */

import { FilterDefinitionType, FilterType } from "@/components/@types";

/** Map UtilityBar / django-style operator keys to strawberry-orm lookup keys. */
const OPERATOR_TO_LOOKUP: Record<string, string> = {
  exact: "exact",
  iExact: "exact",
  iexact: "exact",
  ne: "neq",
  contains: "contains",
  iContains: "i_contains",
  icontains: "i_contains",
  startsWith: "starts_with",
  iStartsWith: "i_starts_with",
  endsWith: "ends_with",
  iEndsWith: "i_ends_with",
  gt: "gt",
  gte: "gte",
  lt: "lt",
  lte: "lte",
  in: "in_list",
  inList: "in_list",
  isNull: "is_null",
  isNotNull: "is_null",
};

type LookupLeaf = Record<string, unknown>;

/** Map legacy reference-chip lookups to nested relation filter inputs. */
function normalizeRelationLookup(lookup: LookupLeaf): LookupLeaf {
  if ("isNull" in lookup || "is_null" in lookup) {
    const isNull = (lookup.is_null ?? lookup.isNull) as boolean;
    return { isNull };
  }
  if ("inList" in lookup || "in_list" in lookup) {
    const inList = (lookup.inList ?? lookup.in_list) as string[];
    return { field: { identifier: { inList } } };
  }
  if ("eq" in lookup) {
    return { field: { identifier: { exact: lookup.eq } } };
  }
  if ("exact" in lookup && typeof lookup.exact === "string") {
    return { field: { identifier: { exact: lookup.exact } } };
  }
  return lookup;
}

function operatorToLookup(operatorKey: string, value: unknown): LookupLeaf {
  const lookupKey = OPERATOR_TO_LOOKUP[operatorKey] ?? operatorKey;
  if (
    lookupKey === "is_null" &&
    (operatorKey === "isNotNull" || operatorKey === "is_not_null")
  ) {
    return { is_null: false };
  }
  return { [lookupKey]: value };
}

type OrmFilterPlacement = "field" | "object" | "topLevel";

/**
 * The key the ledger search bar stores its term under, in the same
 * ``filters`` list as the chips — so it serialises into the URL, clears on
 * navigation and survives a tab switch without any state of its own.
 */
export const SEARCH_FILTER_KEY = "search";

/** Top-level ``@filter_field`` keys on ORM filter inputs (not under ``field``). */
const TOP_LEVEL_FILTER_KEYS = new Set([
  SEARCH_FILTER_KEY,
  "awaitingBankMatch",
  "openJournal",
  "hasPending",
  "posted",
]);

/** Where a chip maps on the strawberry-orm ``@oneOf`` filter input. */
export function ormFilterPlacement(
  filterDefinition: FilterDefinitionType,
  key?: string,
): OrmFilterPlacement {
  if (filterDefinition.type === "reference") {
    return "object";
  }
  if (filterDefinition.type === "boolean") {
    return "topLevel";
  }
  // Through a relation (``documents.type``), the related record's own key.
  const filterKey = (key ?? filterDefinition.key)?.split(".").pop();
  if (filterKey && TOP_LEVEL_FILTER_KEYS.has(filterKey)) {
    return "topLevel";
  }
  return "field";
}

/** Build one ORM filter branch for a column chip. */
export function buildOrmFilterBranch(
  key: string,
  lookup: LookupLeaf,
  placement: OrmFilterPlacement = "field",
): Record<string, unknown> {
  // A key through a relation (``documents.type``) is the related record's own
  // filter, asked of the records the row points at.
  const dot = key.indexOf(".");
  if (dot > 0) {
    return {
      object: {
        [key.slice(0, dot)]: buildOrmFilterBranch(
          key.slice(dot + 1),
          lookup,
          placement,
        ),
      },
    };
  }
  if (placement === "object") {
    // Pointing at none of them: not pointing at any - so a row pointing at
    // several, one of them among these, is left out too.
    if ("ne" in lookup) {
      return { not: buildOrmFilterBranch(key, { eq: lookup.ne }, placement) };
    }
    if ("notInList" in lookup) {
      return {
        not: buildOrmFilterBranch(key, { inList: lookup.notInList }, placement),
      };
    }
    return { object: { [key]: normalizeRelationLookup(lookup) } };
  }
  if (placement === "topLevel") {
    if (typeof lookup === "object" && lookup !== null) {
      if (key === SEARCH_FILTER_KEY) {
        const raw =
          (lookup as { contains?: string }).contains ??
          (lookup as { exact?: string }).exact;
        if (typeof raw === "string") {
          return { search: raw };
        }
      }
      // Boolean ``@filter_field``s take a bare bool (e.g. hasPending, awaitingBankMatch).
      if (typeof (lookup as { exact?: unknown }).exact === "boolean") {
        return { [key]: (lookup as { exact: boolean }).exact };
      }
    }
    return { [key]: lookup };
  }
  return { field: { [key]: lookup } };
}

/**
 * Build a single filter branch from one chip (defaults to scalar ``field``).
 */
export function chipToFilterBranch(
  key: string,
  operatorKey: string,
  value: unknown,
  placement: OrmFilterPlacement = "field",
): Record<string, unknown> {
  return buildOrmFilterBranch(
    key,
    operatorToLookup(operatorKey, value),
    placement,
  );
}

/**
 * Combine active chips into an ORM filter input (``all`` when multiple).
 */
export function chipsToOrmFilter(
  filters: FilterType[],
  getOperatorValue: (
    operatorKey: string,
    value: unknown,
  ) => Record<string, unknown> | undefined,
  filterDefinitions?: Record<string, FilterDefinitionType>,
): Record<string, unknown> | undefined {
  const branches: Record<string, unknown>[] = [];

  for (const filter of filters) {
    if (filter.value === null || filter.value === undefined) {
      continue;
    }
    const lookup =
      getOperatorValue?.(filter.operator, filter.value) ??
      operatorToLookup(filter.operator, filter.value);
    const placement = filterDefinitions?.[filter.key]
      ? ormFilterPlacement(filterDefinitions[filter.key], filter.key)
      : "field";
    branches.push(buildOrmFilterBranch(filter.key, lookup, placement));
  }

  if (branches.length === 0) {
    return undefined;
  }
  if (branches.length === 1) {
    return branches[0];
  }
  return { all: branches };
}

/**
 * Build ORM order input: ``[{ field: { name: ASC } }]``.
 */
export function sortableToOrmOrder(
  sortables: { key: string; value: string }[],
): Record<string, unknown>[] | undefined {
  if (sortables.length === 0) {
    return undefined;
  }
  const { key, value } = sortables[sortables.length - 1];
  return [{ field: { [key]: value } }];
}

/**
 * Ad-hoc filters already shaped as field lookups (e.g. thread list tabs).
 */
export function legacyFieldMapToOrmFilter(
  flat: Record<string, Record<string, unknown>>,
): Record<string, unknown> | undefined {
  const entries = Object.entries(flat);
  if (entries.length === 0) {
    return undefined;
  }
  const branches = entries.map(([key, lookup]) => ({
    field: { [key]: lookup },
  }));
  if (branches.length === 1) {
    return branches[0];
  }
  return { all: branches };
}
